import React, { useCallback, useEffect, useMemo, useState } from 'react';
import AdminShell from '../components/admin/AdminShell';
import {
  fetchAdminEmailCampaigns,
  previewAdminEmailCampaign,
  retryAdminEmailCampaign,
  sendAdminEmailCampaign,
} from '../services/api/admin';
import { getAdminAuthUser } from '../services/storage/adminFlow';
import type {
  AdminEmailAudienceFilters,
  AdminEmailAudienceType,
  AdminEmailCampaign,
  AdminEmailCampaignPayload,
  AdminEmailCampaignPreview,
  AdminEmailSendMode,
} from '../types/admin';
import { formatDateTime } from '../utils/companyOwner';
import '../styles/register.css';
import '../styles/company-owner.css';
import '../styles/admin.css';

const USER_TEMPLATE = `<p>Hi {{name}},</p>
<p>ShiftWagon is going live soon. You can open the app to request or schedule transport when you need a bakkie or truck.</p>
<p>Thank you for joining us early.</p>`;

const DRIVER_TEMPLATE = `<p>Hi {{name}},</p>
<p>ShiftWagon is going live soon, and your driver onboarding status is <strong>{{onboardingStep}}</strong>.</p>
<p>{{recipientGuidance}}</p>
<p>Thank you for being part of the launch driver network.</p>`;

function makeIdempotencyKey(): string {
  const randomPart = typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : Math.random().toString(36).slice(2);
  return `admin-email-${Date.now()}-${randomPart}`;
}

function booleanFromFilter(value: string): boolean | undefined {
  if (value === 'true') return true;
  if (value === 'false') return false;
  return undefined;
}

function statusLabel(value?: string | null): string {
  return (value || 'UNKNOWN').replaceAll('_', ' ');
}

function campaignPillClass(status: string): string {
  if (status === 'SENT') return 'admin-pill admin-pill-success';
  if (status === 'FAILED') return 'admin-pill admin-pill-danger';
  if (status === 'PARTIAL_FAILED') return 'admin-pill admin-pill-warning';
  return 'admin-pill';
}

const AdminCommunicationsPage: React.FC = () => {
  const adminUser = getAdminAuthUser();
  const [audienceType, setAudienceType] = useState<AdminEmailAudienceType>('USERS');
  const [filters, setFilters] = useState({
    query: '',
    role: 'ROLE_USER',
    enabled: 'true',
    locked: '',
    emailVerified: '',
    phoneVerified: '',
    onboardingsStep: '',
    onboardingReviewStatus: '',
    accountType: '',
  });
  const [subject, setSubject] = useState('ShiftWagon is going live');
  const [bodyHtml, setBodyHtml] = useState(USER_TEMPLATE);
  const [bodyText, setBodyText] = useState('');
  const [reason, setReason] = useState('Launch communication to early app signups');
  const [testEmail, setTestEmail] = useState(adminUser?.email || '');
  const [preview, setPreview] = useState<AdminEmailCampaignPreview | null>(null);
  const [campaigns, setCampaigns] = useState<AdminEmailCampaign[]>([]);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [sendingLive, setSendingLive] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [retryingId, setRetryingId] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const selectedFilters = useMemo<AdminEmailAudienceFilters>(() => {
    if (audienceType === 'USERS') {
      return {
        ...(filters.query.trim() ? { query: filters.query.trim() } : {}),
        ...(filters.role ? { role: filters.role } : {}),
        ...(booleanFromFilter(filters.enabled) !== undefined ? { enabled: booleanFromFilter(filters.enabled) } : {}),
        ...(booleanFromFilter(filters.locked) !== undefined ? { locked: booleanFromFilter(filters.locked) } : {}),
        ...(booleanFromFilter(filters.emailVerified) !== undefined
          ? { emailVerified: booleanFromFilter(filters.emailVerified) }
          : {}),
        ...(booleanFromFilter(filters.phoneVerified) !== undefined
          ? { phoneVerified: booleanFromFilter(filters.phoneVerified) }
          : {}),
      };
    }

    return {
      ...(filters.query.trim() ? { query: filters.query.trim() } : {}),
      ...(filters.onboardingsStep ? { onboardingsStep: filters.onboardingsStep } : {}),
      ...(filters.onboardingReviewStatus ? { onboardingReviewStatus: filters.onboardingReviewStatus } : {}),
      ...(filters.accountType ? { accountType: filters.accountType } : {}),
      ...(booleanFromFilter(filters.enabled) !== undefined ? { enabled: booleanFromFilter(filters.enabled) } : {}),
    };
  }, [audienceType, filters]);

  const payloadBase = useCallback((sendMode: AdminEmailSendMode): AdminEmailCampaignPayload => ({
    audienceType,
    filters: selectedFilters,
    subject,
    bodyHtml,
    ...(bodyText.trim() ? { bodyText: bodyText.trim() } : {}),
    sendMode,
    ...(sendMode === 'TEST' ? { testEmail: testEmail.trim() } : {}),
    reason,
    idempotencyKey: makeIdempotencyKey(),
  }), [audienceType, bodyHtml, bodyText, reason, selectedFilters, subject, testEmail]);

  const loadHistory = useCallback(async () => {
    setLoadingHistory(true);
    try {
      const response = await fetchAdminEmailCampaigns({ page: 0, size: 12 });
      setCampaigns(response);
    } catch (loadError: any) {
      setError(loadError.message || 'Could not load campaign history.');
    } finally {
      setLoadingHistory(false);
    }
  }, []);

  useEffect(() => {
    void loadHistory();
  }, [loadHistory]);

  const hasRunningCampaign = campaigns.some((campaign) => campaign.status === 'QUEUED' || campaign.status === 'SENDING');

  useEffect(() => {
    if (!hasRunningCampaign) return undefined;
    const interval = window.setInterval(() => {
      void loadHistory();
    }, 5000);
    return () => window.clearInterval(interval);
  }, [hasRunningCampaign, loadHistory]);

  const handleAudienceChange = (nextAudience: AdminEmailAudienceType) => {
    setAudienceType(nextAudience);
    setPreview(null);
    setSuccess('');
    setError('');
    if (nextAudience === 'DRIVERS') {
      setSubject('ShiftWagon driver launch readiness');
      setBodyHtml(DRIVER_TEMPLATE);
      setFilters((current) => ({
        ...current,
        enabled: 'true',
        onboardingsStep: 'COMPLETED',
        onboardingReviewStatus: 'APPROVED',
      }));
      return;
    }
    setSubject('ShiftWagon is going live');
    setBodyHtml(USER_TEMPLATE);
    setFilters((current) => ({
      ...current,
      role: 'ROLE_USER',
      enabled: 'true',
      emailVerified: '',
    }));
  };

  const handlePreview = async () => {
    setLoadingPreview(true);
    setError('');
    setSuccess('');
    try {
      const response = await previewAdminEmailCampaign(payloadBase('LIVE'));
      setPreview(response);
    } catch (previewError: any) {
      setError(previewError.message || 'Could not preview this campaign.');
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleSend = async (sendMode: AdminEmailSendMode) => {
    const isTest = sendMode === 'TEST';
    if (isTest) {
      setSendingTest(true);
    } else {
      setSendingLive(true);
    }
    setError('');
    setSuccess('');

    try {
      const response = await sendAdminEmailCampaign(payloadBase(sendMode));
      setSuccess(
        isTest
          ? `Test email queued for ${testEmail.trim()}. Refreshing status until it is sent or failed.`
          : `Campaign queued for ${response.recipientCount} recipients. Refreshing status until it completes.`,
      );
      await loadHistory();
    } catch (sendError: any) {
      setError(sendError.message || 'Could not queue this campaign.');
    } finally {
      setSendingTest(false);
      setSendingLive(false);
    }
  };

  const handleRetry = async (campaignId: string) => {
    setRetryingId(campaignId);
    setError('');
    setSuccess('');
    try {
      await retryAdminEmailCampaign(campaignId);
      setSuccess('Campaign retry queued. Refreshing status until it completes.');
      await loadHistory();
    } catch (retryError: any) {
      setError(retryError.message || 'Could not retry this campaign.');
    } finally {
      setRetryingId(null);
    }
  };

  const updateFilter = (key: keyof typeof filters) => (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setPreview(null);
    setFilters((current) => ({ ...current, [key]: event.target.value }));
  };

  return (
    <AdminShell
      badge="Communications"
      title="Admin communications"
      copy="Compose launch and onboarding emails, preview the resolved audience, send tests, and queue audited campaigns."
    >
      {error ? <div className="register-inline-error">{error}</div> : null}
      {success ? <div className="register-inline-success">{success}</div> : null}

      <section className="admin-panel admin-communications-panel">
        <div className="admin-panel-header">
          <div>
            <h2 className="admin-panel-title">Audience</h2>
            <p className="admin-panel-copy">Choose who should receive this email. User sends default to customer accounts.</p>
          </div>
          <div className="admin-segmented-control" role="group" aria-label="Audience type">
            <button
              className={audienceType === 'USERS' ? 'admin-segment-active' : ''}
              onClick={() => handleAudienceChange('USERS')}
              type="button"
            >
              Users
            </button>
            <button
              className={audienceType === 'DRIVERS' ? 'admin-segment-active' : ''}
              onClick={() => handleAudienceChange('DRIVERS')}
              type="button"
            >
              Drivers
            </button>
          </div>
        </div>

        <div className="admin-toolbar admin-communications-toolbar">
          <input
            className="register-input admin-toolbar-wide"
            onChange={updateFilter('query')}
            placeholder={audienceType === 'USERS' ? 'Search users by name or email' : 'Search drivers by name, email, or phone'}
            value={filters.query}
          />
          {audienceType === 'USERS' ? (
            <>
              <select className="register-input" onChange={updateFilter('role')} value={filters.role}>
                <option value="ROLE_USER">Customers</option>
                <option value="ROLE_COMPANY_OWNER">Company owners</option>
                <option value="">All user roles</option>
              </select>
              <select className="register-input" onChange={updateFilter('emailVerified')} value={filters.emailVerified}>
                <option value="">Any email state</option>
                <option value="true">Email verified</option>
                <option value="false">Email pending</option>
              </select>
              <select className="register-input" onChange={updateFilter('phoneVerified')} value={filters.phoneVerified}>
                <option value="">Any phone state</option>
                <option value="true">Phone verified</option>
                <option value="false">Phone pending</option>
              </select>
            </>
          ) : (
            <>
              <select className="register-input" onChange={updateFilter('onboardingsStep')} value={filters.onboardingsStep}>
                <option value="">All steps</option>
                <option value="COMPLETED">Completed</option>
                <option value="SUBMITTED">Submitted</option>
                <option value="BANK_DETAILS">Bank details</option>
                <option value="COMPANY_DETAILS">Company details</option>
                <option value="ACCOUNT_TYPE">Account type</option>
                <option value="VEHICLE_PHOTOS">Vehicle photos</option>
                <option value="VEHICLE_DETAILS">Vehicle details</option>
                <option value="PERSONAL_DOCS">Personal docs</option>
                <option value="PHONE_VERIFIED">Phone verified</option>
                <option value="EMAIL_VERIFIED">Email verified</option>
              </select>
              <select className="register-input" onChange={updateFilter('onboardingReviewStatus')} value={filters.onboardingReviewStatus}>
                <option value="">Any review state</option>
                <option value="APPROVED">Approved</option>
                <option value="PENDING">Pending</option>
                <option value="REJECTED">Rejected</option>
              </select>
              <select className="register-input" onChange={updateFilter('accountType')} value={filters.accountType}>
                <option value="">All account types</option>
                <option value="INDIVIDUAL_OWNER">Individual owner</option>
                <option value="COMPANY_DRIVER">Company driver</option>
              </select>
            </>
          )}
          <select className="register-input" onChange={updateFilter('enabled')} value={filters.enabled}>
            <option value="true">Enabled</option>
            <option value="false">Disabled</option>
            <option value="">Enabled or disabled</option>
          </select>
        </div>
      </section>

      <div className="admin-grid admin-communications-layout">
        <section className="admin-panel">
          <div className="admin-panel-header">
            <div>
              <h2 className="admin-panel-title">Compose email</h2>
              <p className="admin-panel-copy">Optional merge fields: {'{{name}}'}, {'{{onboardingStep}}'}, {'{{recipientGuidance}}'}.</p>
            </div>
          </div>

          <div className="register-form admin-communications-form">
            <label className="register-label" htmlFor="campaignSubject">Subject</label>
            <input
              className="register-input"
              id="campaignSubject"
              onChange={(event) => {
                setSubject(event.target.value);
                setPreview(null);
              }}
              value={subject}
            />

            <label className="register-label" htmlFor="campaignBody">Email body</label>
            <textarea
              className="register-input admin-communications-body"
              id="campaignBody"
              onChange={(event) => {
                setBodyHtml(event.target.value);
                setPreview(null);
              }}
              value={bodyHtml}
            />

            <label className="register-label" htmlFor="campaignText">Plain text fallback</label>
            <textarea
              className="register-input"
              id="campaignText"
              onChange={(event) => setBodyText(event.target.value)}
              placeholder="Optional plain text version"
              rows={4}
              value={bodyText}
            />

            <label className="register-label" htmlFor="campaignReason">Admin reason</label>
            <input
              className="register-input"
              id="campaignReason"
              onChange={(event) => setReason(event.target.value)}
              value={reason}
            />

            <label className="register-label" htmlFor="campaignTestEmail">Test email</label>
            <input
              className="register-input"
              id="campaignTestEmail"
              onChange={(event) => setTestEmail(event.target.value)}
              type="email"
              value={testEmail}
            />

            <div className="admin-inline-actions">
              <button className="register-secondary-button" disabled={loadingPreview} onClick={handlePreview} type="button">
                {loadingPreview ? 'Previewing...' : 'Preview audience'}
              </button>
              <button className="register-secondary-button" disabled={sendingTest || !testEmail.trim()} onClick={() => void handleSend('TEST')} type="button">
                {sendingTest ? 'Sending test...' : 'Send test'}
              </button>
              <button
                className="register-submit register-submit-inline"
                disabled={sendingLive || !preview || preview.estimatedRecipients === 0}
                onClick={() => void handleSend('LIVE')}
                type="button"
              >
                {sendingLive ? 'Queueing...' : 'Queue live campaign'}
              </button>
            </div>
            {!preview ? (
              <p className="admin-muted-note">
                Preview the audience first to unlock the live campaign button. Sending a test does not unlock live send.
              </p>
            ) : null}
            {preview && preview.estimatedRecipients === 0 ? (
              <p className="admin-muted-note">
                The selected filters returned no recipients, so live send is disabled.
              </p>
            ) : null}
          </div>
        </section>

        <section className="admin-panel">
          <div className="admin-panel-header">
            <div>
              <h2 className="admin-panel-title">Preview</h2>
              <p className="admin-panel-copy">Review count and sample recipients before queueing a live send.</p>
            </div>
          </div>

          {preview ? (
            <>
              <div className="admin-communications-count">
                <span>Recipients</span>
                <strong>{preview.estimatedRecipients}</strong>
              </div>
              <div className="admin-pill-row">
                <span className="admin-pill">{preview.audienceType}</span>
                {preview.warnings.map((warning) => (
                  <span className="admin-pill admin-pill-warning" key={warning}>{warning}</span>
                ))}
              </div>
              <div className="admin-list admin-communications-sample-list">
                {preview.sampleRecipients.map((recipient) => (
                  <div className="admin-list-card" key={`${recipient.recipientType}-${recipient.recipientId}-${recipient.email}`}>
                    <strong>{recipient.name || recipient.email}</strong>
                    <small>{recipient.email}</small>
                    <small>{recipient.statusSummary || recipient.recipientType}</small>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <p className="admin-empty">Preview the audience to see who this send will reach.</p>
          )}
        </section>
      </div>

      <section className="admin-panel">
          <div className="admin-panel-header">
            <div>
              <h2 className="admin-panel-title">Campaign history</h2>
              <p className="admin-panel-copy">
                Recent admin email campaigns from notification-service. Queued or sending rows refresh every 5 seconds.
              </p>
            </div>
          <button className="register-secondary-button" disabled={loadingHistory} onClick={() => void loadHistory()} type="button">
            {loadingHistory ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>

        <div className="admin-table">
          <div className="admin-table-head admin-table-head-communications">
            <span>Campaign</span>
            <span>Status</span>
            <span>Recipients</span>
            <span>Created</span>
          </div>
          {campaigns.map((campaign) => (
            <div className="admin-table-row admin-table-row-communications" key={campaign.id}>
              <span>
                <strong>{campaign.subject}</strong>
                <small>{campaign.reason || 'No reason supplied'}</small>
                <small>{campaign.createdByAdminEmail || 'Admin'}</small>
              </span>
              <span>
                <span className={campaignPillClass(campaign.status)}>{statusLabel(campaign.status)}</span>
                <small>{campaign.sendMode}</small>
              </span>
              <span>
                <strong>{campaign.sentCount}/{campaign.recipientCount} sent</strong>
                <small>{campaign.failedCount} failed - {campaign.skippedCount} skipped</small>
              </span>
              <span>
                <strong>{formatDateTime(campaign.created)}</strong>
                <small>{campaign.completedAt ? `Completed ${formatDateTime(campaign.completedAt)}` : 'Not completed yet'}</small>
                {campaign.failureMessage ? <small>{campaign.failureMessage}</small> : null}
                {(campaign.status === 'QUEUED' || campaign.status === 'FAILED' || campaign.status === 'PARTIAL_FAILED') ? (
                  <button
                    className="admin-link-button"
                    disabled={retryingId === campaign.id}
                    onClick={() => void handleRetry(campaign.id)}
                    type="button"
                  >
                    {retryingId === campaign.id ? 'Retrying...' : 'Retry send'}
                  </button>
                ) : null}
              </span>
            </div>
          ))}
          {!loadingHistory && campaigns.length === 0 ? (
            <p className="admin-empty">No campaigns have been queued yet.</p>
          ) : null}
        </div>
      </section>
    </AdminShell>
  );
};

export default AdminCommunicationsPage;
